import React, { useState, useEffect } from 'react';
import { 
  getUserTags, 
  createTag, 
  updateTag, 
  deleteTag, 
  getFilesByTag,
  getAITagSuggestions 
} from '../../services/api';

const TagManager = ({ className = "", onTagsChange = null, selectedFiles = [] }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [selectedTagFiles, setSelectedTagFiles] = useState([]);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [tagToApply, setTagToApply] = useState(null);
  const [selectedForTagging, setSelectedForTagging] = useState(new Set());

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    icon: 'tag'
  });

  const colorOptions = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6B7280'
  ];

  const iconOptions = [
    'tag', 'receipt', 'file-alt', 'folder', 'star', 'bookmark',
    'flag', 'heart', 'thumbtack', 'paperclip', 'shield-alt', 'legal'
  ];

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const userTags = await getUserTags();
      setTags(userTags);
      if (onTagsChange) onTagsChange(userTags);
    } catch (err) {
      setError(`Failed to load tags: ${err.message}`);
      console.error('Error loading tags:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    try {
      await createTag(formData);
      setShowCreateModal(false);
      setFormData({ name: '', description: '', color: '#3B82F6', icon: 'tag' });
      await loadTags();
    } catch (err) {
      alert(`Failed to create tag: ${err.message}`);
    }
  };

  const handleEditTag = async (e) => {
    e.preventDefault();
    try {
      await updateTag(editingTag.tag_id, formData);
      setShowEditModal(false);
      setEditingTag(null);
      setFormData({ name: '', description: '', color: '#3B82F6', icon: 'tag' });
      await loadTags();
    } catch (err) {
      alert(`Failed to update tag: ${err.message}`);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (window.confirm(`Are you sure you want to delete the tag "${tag.name}"? This will remove it from all files.`)) {
      try {
        await deleteTag(tag.tag_id);
        await loadTags();
      } catch (err) {
        alert(`Failed to delete tag: ${err.message}`);
      }
    }
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description,
      color: tag.color,
      icon: tag.icon
    });
    setShowEditModal(true);
  };

  const viewTagFiles = async (tag) => {
    try {
      const result = await getFilesByTag(tag.tag_id);
      setSelectedTagFiles(result.files || []);
      setShowFilesModal(true);
    } catch (err) {
      alert(`Failed to load files for tag: ${err.message}`);
    }
  };

  const handleAISuggestion = async () => {
    try {
      setAiSuggesting(true);
      // This would typically require file IDs, but for demo we'll show the concept
      alert('AI tag suggestions feature coming soon! This will analyze your files and suggest new tags.');
    } catch (err) {
      alert(`AI suggestion failed: ${err.message}`);
    } finally {
      setAiSuggesting(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-400"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading tags...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fas fa-exclamation-triangle text-red-400"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading tags</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button 
                onClick={loadTags}
                className="mt-2 text-sm text-red-800 underline hover:text-red-900"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleTagClick = (tag) => {
    setTagToApply(tag);
    setSelectedForTagging(new Set());
    setShowApplyModal(true);
  };

  const handleApplyTagToFiles = async () => {
    if (!tagToApply || selectedForTagging.size === 0) return;
    
    try {
      // In a real app, this would make API calls to apply tags to files
      console.log(`Applying tag "${tagToApply.name}" to ${selectedForTagging.size} files:`, Array.from(selectedForTagging));
      
      // Simulate success
      alert(`Successfully applied tag "${tagToApply.name}" to ${selectedForTagging.size} file(s)!`);
      
      setShowApplyModal(false);
      setTagToApply(null);
      setSelectedForTagging(new Set());
    } catch (error) {
      alert(`Failed to apply tag: ${error.message}`);
    }
  };

  const handleFileSelection = (fileKey, isSelected) => {
    setSelectedForTagging(prev => {
      const newSelected = new Set(prev);
      if (isSelected) {
        newSelected.add(fileKey);
      } else {
        newSelected.delete(fileKey);
      }
      return newSelected;
    });
  };

  const handleSelectAllFiles = () => {
    if (selectedForTagging.size === selectedFiles.length) {
      setSelectedForTagging(new Set());
    } else {
      setSelectedForTagging(new Set(selectedFiles.map(f => f.file_key || f.id)));
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            TAG ({tags.length})
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Click on any tag to apply it to your files
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleAISuggestion}
            disabled={aiSuggesting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aiSuggesting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                AI Suggesting...
              </>
            ) : (
              <>
                <i className="fas fa-magic mr-2"></i>
                AI Suggest
              </>
            )}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
          >
            <i className="fas fa-plus mr-2"></i>
            Create Tag
          </button>
        </div>
      </div>

      {/* Tags List */}
      {tags.length === 0 ? (
        <div className="text-center py-12">
          <i className="fas fa-tags text-4xl text-gray-400 dark:text-gray-500 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No tags yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Create your first tag to start organizing your files
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
          >
            <i className="fas fa-plus mr-2"></i>
            Create Your First Tag
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          {/* Tag List */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {tags.map((tag, index) => (
              <div
                key={tag.tag_id}
                className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer group"
                onClick={() => handleTagClick(tag)}
              >
                <div className="flex items-center space-x-3">
                  {/* Clean tag display like in your image */}
                  <div className="bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded text-sm font-mono text-gray-800 dark:text-gray-200 border">
                    {tag.name}
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {tag.usage_count || 0} uses
                  </span>
                </div>
                
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      viewTagFiles(tag);
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 transition duration-200"
                    title="View files"
                  >
                    <i className="fas fa-eye text-sm"></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(tag);
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 transition duration-200"
                    title="Edit tag"
                  >
                    <i className="fas fa-edit text-sm"></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTag(tag);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 transition duration-200"
                    title="Delete tag"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Apply Tag Modal */}
      {showApplyModal && tagToApply && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Apply Tag: "{tagToApply.name}"
              </h3>
              <button
                onClick={() => setShowApplyModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select the files you want to apply this tag to:
            </p>
            
            {selectedFiles.length === 0 ? (
              <div className="text-center py-8">
                <i className="fas fa-file text-4xl text-gray-400 dark:text-gray-500 mb-4"></i>
                <p className="text-gray-500 dark:text-gray-400">No files available</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">Upload some files first to apply tags</p>
              </div>
            ) : (
              <>
                {/* Select All Button */}
                <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedForTagging.size === selectedFiles.length && selectedFiles.length > 0}
                      onChange={handleSelectAllFiles}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Select All ({selectedFiles.length} files)
                    </span>
                  </label>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedForTagging.size} selected
                  </span>
                </div>
                
                {/* File List */}
                <div className="max-h-60 overflow-y-auto space-y-2 mb-6">
                  {selectedFiles.map((file) => {
                    const fileKey = file.file_key || file.id || file.original_name;
                    const isSelected = selectedForTagging.has(fileKey);
                    
                    return (
                      <div
                        key={fileKey}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => handleFileSelection(fileKey, !isSelected)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleFileSelection(fileKey, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {file.original_name || file.filename}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Size unknown'}
                          </p>
                        </div>
                        {/* File type icon */}
                        <div className="flex-shrink-0 ml-3">
                          <i className="fas fa-file text-gray-400"></i>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Apply Button */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowApplyModal(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApplyTagToFiles}
                    disabled={selectedForTagging.size === 0}
                    className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded transition duration-200"
                  >
                    Apply to {selectedForTagging.size} file(s)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Tag Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Tag</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleCreateTag} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tag Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., Expenses, Legal, Important"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="What this tag represents..."
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        formData.color === color ? 'border-gray-800 dark:border-white' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`p-2 rounded border ${
                        formData.icon === icon 
                          ? 'border-red-400 bg-red-50 dark:bg-red-900' 
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <i className={`fas fa-${icon} text-gray-600 dark:text-gray-400`}></i>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
                >
                  Create Tag
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tag Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Tag</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleEditTag} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tag Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        formData.color === color ? 'border-gray-800 dark:border-white' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`p-2 rounded border ${
                        formData.icon === icon 
                          ? 'border-red-400 bg-red-50 dark:bg-red-900' 
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <i className={`fas fa-${icon} text-gray-600 dark:text-gray-400`}></i>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
                >
                  Update Tag
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Files Modal */}
      {showFilesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Files with this tag ({selectedTagFiles.length})
              </h3>
              <button
                onClick={() => setShowFilesModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {selectedTagFiles.length === 0 ? (
              <div className="text-center py-8">
                <i className="fas fa-file text-4xl text-gray-400 dark:text-gray-500 mb-4"></i>
                <p className="text-gray-500 dark:text-gray-400">No files have this tag yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedTagFiles.map((file, index) => (
                  <div key={index} className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <i className="fas fa-file text-blue-500 mr-3"></i>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{file.filename}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Size unknown'} • 
                        {file.last_modified ? new Date(file.last_modified).toLocaleDateString() : 'Date unknown'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagManager;



